import React, { FC, useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, Autocomplete, TextField, CircularProgress, Theme } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { Modal } from '../../../components/Modal'
import { IData } from '../UserProfileTabs'
import { introducerAssignResolver } from './resolvers'
import { getAssignableReferralsData, usersForReassignIsLoading } from '../../../store/selectors'
import { assignIntroducerUserRequest, getAssignableReferralsRequest } from '../../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    color: theme.palette.primary.light,
    '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
    },
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiIconButton-root': {
        color: theme.palette.primary.light,
      },
    },
  },
  disabled: {
    color: theme.palette.primary.light,
    pointerEvents: 'none',
    '& .MuiOutlinedInput-root': {
      cursor: 'default',
    },
    '& .MuiAutocomplete-endAdornment': {
      display: 'none',
    },
  },
  noOptions: {
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },
  anim: {
    color: '#6BFFBF',
    animationDuration: '550ms',
  },
  circle: {
    strokeLinecap: 'round',
  },
}))

interface IntroducerAssignModalProps extends Pick<IData, 'idSf'> {
  introducerId?: IData['id']
  isOpen: boolean
  modalClose: () => void
}

export const IntroducerAssignModal: FC<IntroducerAssignModalProps> = ({ isOpen, modalClose, idSf, introducerId }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const loading = useSelector(usersForReassignIsLoading)
  const items = useSelector(getAssignableReferralsData)
  const filteredItems = useMemo(() => items.filter((user) => user.idSf !== idSf), [items, idSf])

  const formMethods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: introducerAssignResolver,
  })
  const {
    reset,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = formMethods

  const selectedUser = watch('assignUser')

  const onSubmit = useCallback(
    (data) => {
      if (introducerId)
        dispatch(
          assignIntroducerUserRequest({
            introducerId,
            referredUserId: +data.assignUser,
          }),
        )
      reset?.()
      modalClose?.()
    },
    [reset, modalClose, dispatch, introducerId],
  )

  const fetchUsers = useCallback(() => {
    dispatch(getAssignableReferralsRequest())
  }, [dispatch])

  return (
    <Modal
      title={'Assign user'}
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!selectedUser}
      width={524}
    >
      <FormProvider {...formMethods}>
        <Grid container direction="column" gap={2}>
          <Grid item xs={12} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h6">Introducer</Typography>
            </Grid>
            <Grid item xs={8}>
              <Autocomplete
                value={idSf}
                disableClearable
                options={idSf ? [idSf] : []}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField {...params} className={clsx(classes.select, classes.disabled)} fullWidth />
                )}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h6">Referred User</Typography>
            </Grid>
            <Grid item xs={8}>
              <Autocomplete
                loading={loading}
                loadingText={
                  <CircularProgress
                    variant="indeterminate"
                    disableShrink
                    className={classes.anim}
                    classes={{
                      circle: classes.circle,
                    }}
                    size={24}
                    thickness={4}
                  />
                }
                onOpen={fetchUsers}
                options={filteredItems}
                noOptionsText={'User not found'}
                getOptionLabel={(option) => `${option.name} (${option.idSf})`}
                isOptionEqualToValue={(option, value) => option.id === (value?.id || value)}
                value={filteredItems.find((user) => user.id === selectedUser) || null}
                onChange={(_, value) => setValue('assignUser', value ? value.id : null)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name} ({option.idSf})
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    className={classes.select}
                    placeholder="Select user"
                    error={!!errors.assignUser}
                    helperText={errors.assignUser?.message}
                    aria-label="Select user"
                  />
                )}
                classes={{
                  noOptions: classes.noOptions,
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}
